/* eslint-disable jira/filename-case */
import type { PropsWithChildren, RefObject } from 'react';
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import type {
	CreatedIssueWithIssueData,
	FormFieldValues,
	Context,
	FieldConfig,
	Project,
} from '@jira/issue-create__issue-create-common-types/src/common/types/index.tsx';
import type {
	JIRA_ISSUE_COMMENT,
	ALLOWED_EXTERNAL_CONTROLS,
	COMMAND_PALETTE_CREATE_LINKED_ISSUE,
	JSW_INCIDENTS_CREATE_ISSUE_TRIGGER,
	GENERIC_FORGE_USE_PRODUCT_MODALS,
	GENERIC_GLOBAL_CREATE_BUTTON,
	GENERIC_KEYBOARD_SHORTCUT,
	JSW_ISSUE_VIEW_CREATE_ISSUE_IN_EPIC,
	JSW_ISSUE_VIEW_CREATE_SUBTASK,
	JSW_ISSUE_VIEW_AI_BREAKDOWN_CHILD_ISSUE,
	JSW_ISSUE_VIEW_AI_BREAKDOWN_SUBTASK,
	JSW_RELEASES_CREATE_ISSUE,
	JSW_ROADMAP_CREATE_ISSUE,
	JSW_SECURITY_CREATE_ISSUE,
	JSM_CALENDAR_CREATE_CHANGE_REQUEST,
	JSM_EMPTY_QUEUE_CREATE_REQUEST,
	JSM_PROJECT_SUMMARY_CREATE_REQUEST,
	JWM_BOARD_CREATE_ISSUE,
	JWM_BOARD_EMBED_ISSUE_CREATE,
	JWM_CALENDAR_CREATE_ISSUE,
	JWM_INLINE_CREATE,
	BOARD_CREATE,
	JWM_LIST_CREATE_ISSUE,
	JWM_LIST_CREATE_SUBTASK,
	JWM_LIST_EMBED_ISSUE_CREATE,
	JWM_SUMMARY_CREATE_FIRST_ISSUE_TRIGGER,
	JWM_TIMELINE_CREATE_ISSUE,
	JWM_TIMELINE_CREATE_SUBTASK,
	JSW_CREATE_ISSUE_DIALOG,
	ISSUE_VIEW_CREATE_LINKED_ISSUE,
	SERVICE_DESK_LINKED_ISSUE_TRIGEER,
	JSW_INCIDENTS_CREATE_JSM_PIR_TRIGGER,
	JPO_DIALOGS_CREATE_ISSUE_TRIGGER,
	JSW_ADAPTED_GLOBAL_ISSUE_CREATE,
	JSW_CREATE_EPIC_ISSUE,
	CREATE_ISSUE_BACKLOG_TRIGGER,
	CREATE_EPIC_ISSUE_BACKLOG_TRIGGER,
	CREATE_ISSUE_IN_EPIC_BACKLOG_TRIGGER,
	CREATE_ISSUE_ACTIVE_SPRINT_EMPTY_VIEW,
	CREATE_LINKED_ISSUE_NAVIGATOR,
	CREATE_SUBTASK_ISSUE_NAVIGATOR_TRIGGER,
	CREATE_SUBTASK_NIN_TRIGGER,
	TRIGGER_POINT_KEY_NOT_DEFINED,
	ISSUE_PARENT_SWITCHER_FIELD_CREATE_ISSUE,
	POLARIS_CREATE_DELIVERY_ISSUE,
	HOME_PAGE_DEFAULT_OPEN_GIC,
	ISSUE_CREATE_EMBEDDABLE_TRIGGER,
	JSM_CREATE_INCIDENT_FROM_ALERT_DETAIL,
	JSM_CREATE_INCIDENT_FROM_ALERT_LIST,
	JSM_OPS_XFLOW_ONBOARDING_CREATE,
	DEVELOPER_ESCALATIONS_ISSUE_CREATE_TRIGGER,
	POLARIS_GLOBAL_SUBMIT_IDEA_SWITCH_PROJECTS_TRIGGER,
	JIRA_CALENDAR_CREATE_ISSUE,
	MOBILE_GLOBAL_CREATE_BUTTON,
	JSM_CREATE_PROBLEM_FROM_INCIDENT_AI,
	JIRA_INBOX_CREATE_WORK_ITEM,
	UC_ATLASSIAN_HOME_CREATE_ISSUE,
	JIRA_ISSUE_TABLE_TRIGGER,
} from './constants.tsx';

type CallbackPayload = {
	id: string;
	data: {
		[key: string]: unknown;
	};
};

/**
 * Controls whether GIC creates an issue on submit or saves field edits without creation.
 * Used to support "Save to draft" flows (e.g. Rovo AI create work preview card inline edit).
 */
export const ISSUE_CREATE_SUBMIT_BEHAVIOUR = {
	CREATE_ISSUE: 'createIssue',
	SAVE_EDITS_WITHOUT_ISSUE_CREATION: 'saveEditsWithoutIssueCreation',
} as const;

export type IssueCreateSubmitBehaviour =
	(typeof ISSUE_CREATE_SUBMIT_BEHAVIOUR)[keyof typeof ISSUE_CREATE_SUBMIT_BEHAVIOUR];

export type PersistentIssueCreateModalView = 'modal' | 'mini';

export type MinimizableModalView = 'modal' | 'minimized' | 'mini' | 'fullscreen';

export type TriggerPointKeyType =
	| typeof GENERIC_FORGE_USE_PRODUCT_MODALS
	| typeof GENERIC_GLOBAL_CREATE_BUTTON
	| typeof GENERIC_KEYBOARD_SHORTCUT
	| typeof JSW_ISSUE_VIEW_CREATE_ISSUE_IN_EPIC
	| typeof JSW_ISSUE_VIEW_CREATE_SUBTASK
	| typeof JSW_ISSUE_VIEW_AI_BREAKDOWN_CHILD_ISSUE
	| typeof JSW_ISSUE_VIEW_AI_BREAKDOWN_SUBTASK
	| typeof JSW_RELEASES_CREATE_ISSUE
	| typeof JSW_ROADMAP_CREATE_ISSUE
	| typeof JSW_SECURITY_CREATE_ISSUE
	| typeof JSM_CALENDAR_CREATE_CHANGE_REQUEST
	| typeof JSM_CREATE_INCIDENT_FROM_ALERT_DETAIL
	| typeof JSM_CREATE_INCIDENT_FROM_ALERT_LIST
	| typeof JSM_OPS_XFLOW_ONBOARDING_CREATE
	| typeof JSM_EMPTY_QUEUE_CREATE_REQUEST
	| typeof JSM_PROJECT_SUMMARY_CREATE_REQUEST
	| typeof JWM_BOARD_CREATE_ISSUE
	| typeof JWM_BOARD_EMBED_ISSUE_CREATE
	| typeof JWM_CALENDAR_CREATE_ISSUE
	| typeof JWM_INLINE_CREATE
	| typeof BOARD_CREATE
	| typeof JWM_LIST_CREATE_ISSUE
	| typeof JWM_LIST_CREATE_SUBTASK
	| typeof JWM_LIST_EMBED_ISSUE_CREATE
	| typeof JWM_SUMMARY_CREATE_FIRST_ISSUE_TRIGGER
	| typeof JWM_TIMELINE_CREATE_ISSUE
	| typeof JWM_TIMELINE_CREATE_SUBTASK
	| typeof JSW_CREATE_ISSUE_DIALOG
	| typeof JIRA_CALENDAR_CREATE_ISSUE
	| typeof ISSUE_VIEW_CREATE_LINKED_ISSUE
	| typeof COMMAND_PALETTE_CREATE_LINKED_ISSUE
	| typeof SERVICE_DESK_LINKED_ISSUE_TRIGEER
	| typeof JSW_INCIDENTS_CREATE_JSM_PIR_TRIGGER
	| typeof JSW_INCIDENTS_CREATE_ISSUE_TRIGGER
	| typeof JPO_DIALOGS_CREATE_ISSUE_TRIGGER
	| typeof JSW_ADAPTED_GLOBAL_ISSUE_CREATE
	| typeof JSW_CREATE_EPIC_ISSUE
	| typeof CREATE_ISSUE_BACKLOG_TRIGGER
	| typeof CREATE_ISSUE_IN_EPIC_BACKLOG_TRIGGER
	| typeof CREATE_EPIC_ISSUE_BACKLOG_TRIGGER
	| typeof CREATE_ISSUE_ACTIVE_SPRINT_EMPTY_VIEW
	| typeof CREATE_LINKED_ISSUE_NAVIGATOR
	| typeof CREATE_SUBTASK_ISSUE_NAVIGATOR_TRIGGER
	| typeof CREATE_SUBTASK_NIN_TRIGGER
	| typeof TRIGGER_POINT_KEY_NOT_DEFINED
	| typeof ISSUE_PARENT_SWITCHER_FIELD_CREATE_ISSUE
	| typeof POLARIS_CREATE_DELIVERY_ISSUE
	| typeof HOME_PAGE_DEFAULT_OPEN_GIC
	| typeof ISSUE_CREATE_EMBEDDABLE_TRIGGER
	| typeof DEVELOPER_ESCALATIONS_ISSUE_CREATE_TRIGGER
	| typeof POLARIS_GLOBAL_SUBMIT_IDEA_SWITCH_PROJECTS_TRIGGER
	| typeof MOBILE_GLOBAL_CREATE_BUTTON
	| typeof JSM_CREATE_PROBLEM_FROM_INCIDENT_AI
	| typeof JIRA_ISSUE_COMMENT
	| typeof JIRA_INBOX_CREATE_WORK_ITEM
	| typeof JIRA_ISSUE_TABLE_TRIGGER
	| typeof UC_ATLASSIAN_HOME_CREATE_ISSUE;

type SectionType = 'info' | 'error';
export type SectionMessage = {
	type: SectionType;
	title: string;
	meta: {
		[key: string]: string;
	};
	displayList: string[];
};

type ContainerStyle = {
	paddingLeft?: string;
	paddingRight?: string;
	backgroundColor?: string;
};

export type AdditionalContext = {
	jqls?: string[];
	linkedIssue?: {
		sourceIssueId: string;
		includeLinks?: boolean;
		includeAttachments?: boolean;
	};
};

type ParentIssueDetails = {
	parentId: string;
};

export type Field = {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: any;
};

export type IssueTriggerPayload = {
	project?: {
		projectId: string;
		/** Optional project metadata - provided when switching between old and new modals */
		label?: string;
		iconUrl?: string;
		projectKey?: string;
		projectType?: ProjectType;
		isSimplified?: boolean;
	};
	issueType?: {
		issueTypeId: string;
	};
	issueTypes?: string;
	requestType?: {
		key: string;
	};
	status?: {
		statusId: string;
	};
	requestTypes?: string;
	defaultValues?: Field;
	parentId?: ParentIssueDetails;
	additionalContext?: AdditionalContext;
	customFieldsToPreserveOnFormRecreation?: string[];
	overrideCloudId?: string;
};

export type ExternalControlsFieldAPI = Record<
	(typeof ALLOWED_EXTERNAL_CONTROLS.FIELD_IDS)[number],
	{
		getValue: () => unknown;
		setValue: (value: unknown) => void;
	}
>;
export type ExternalControlsType = {
	Component: (props: {
		getFormFieldValue: (fieldId: string) => unknown;
		fieldAPI: ExternalControlsFieldAPI;
	}) => React.ReactNode;
	triggerPoint: (typeof ALLOWED_EXTERNAL_CONTROLS.TRIGGER_POINTS)[number];
};

export type State = {
	inProgressIssueID?: string;
	isModalOpen: boolean;
	isSubsequentLoad: boolean;
	wasStateRestored: boolean;
	isSubTaskCreationOpen: boolean;
	isCreateLinkedIssue: boolean;
	payload: IssueTriggerPayload | null;
	id: string;
	title: string | null;
	globalEventNamespaces: string[];
	sectionMessage: SectionMessage | null;
	fieldsToAddInPublishedData: string[];
	alertsToBeLinked?: string[];
	onIssueCreate: null | ((data: CreatedIssueWithIssueData, alertsToBeLinked?: string[]) => void);
	onClose: null | ((data: CreatedIssueWithIssueData[]) => void);
	onSiteSelection: null | ((cloudId: string) => void);
	onProjectSelection: null | ((project: Project) => void);
	displayOnlyRequiredFields: boolean;
	displaySitePicker: boolean;
	disableCreationSuccessFlag: boolean;
	displayCreateAnotherIssueToggle: boolean;
	disableProjectDropdown: boolean;
	disableIssueTypeDropdown: boolean;
	triggerPointKey: TriggerPointKeyType;
	context?: Context;
	minimizableModalView: MinimizableModalView;
	persistentModalView?: PersistentIssueCreateModalView;
	wasModalRetriggered: boolean;
	openIssueCreateModalNextState: Partial<State>;
	shouldInitFetchConfig: boolean;
	interactedWithModalChangerButtons?: boolean;
	callbackPayload?: CallbackPayload;
	isModalLoadInContext: boolean;
	disableMinimizableModal: boolean;
	submitErrorMessage?: string;
	shouldPushMiniModalDiscoverability?: boolean;
	shouldShowMiniModalSpotlight?: boolean;
	displayMinimizableButtons?: boolean;
	displayHeaderActionContainer?: boolean;
	overrideUseRequestTypeFieldsToggleState?: boolean;
	containerStyle?: ContainerStyle;
	isMiniDesignEnabled?: boolean;
	isEnrolledAndTargetedForGicMultipleLayouts?: boolean;
	sessionId?: string;
	FieldWrapperComponent?: FieldWrapperComponentType;
	ExternalControls?: ExternalControlsType;
	jqlSwimlaneId?: string;
	prefilledCustomFields?: Field;
	ecosystemCallback?: { callbackFunction: Function };
	overrideCloudId?: string;
	product?: string;
	actionSource?: string;
	triggeredViaHotKey?: boolean;
	optOutSmartFieldIds?: string[];
	triggerButtonRef?: HTMLButtonElement | null;
	addChildWorkItemTriggerSource?: string;
	returnFocusRef?: RefObject<HTMLElement>;
	/** When true, indicates the user was redirected from the modern modal to the old modal (e.g., for unsupported project types) */
	isRedirectedFromModernModal?: boolean;
	/** When true, a transition between old and new modal is in progress (switchToModernModal or triggerFallbackToOldModal). Cleared when isModalOpen is set to false. */
	isTransitioningBetweenOldAndNewModal: boolean;
	/**
	 * When true, backend forceLegacyCreateExperience required us to fall back to the legacy modal
	 * even though the project type is supported by the modernised modal.
	 */
	shouldUseLegacyCreateExperience?: boolean;
	/**
	 * Controls whether GIC creates an issue on submit or fires onSaveEditsWithoutIssueCreation.
	 * Defaults to 'createIssue' if not provided.
	 */
	submitBehaviour?: IssueCreateSubmitBehaviour;
	/**
	 * Called with the full form field payload when submitBehaviour is
	 * 'saveEditsWithoutIssueCreation' and the user clicks the submit button.
	 * No issue is created in this case.
	 */
	onSaveEditsWithoutIssueCreation?: (fields: FormFieldValues) => void;
};

export type GICPayload = {
	payload?: IssueTriggerPayload | null;
	isSubTaskCreationOpen?: boolean;
	isCreateLinkedIssue?: boolean;
	id?: string;
	title?: string | null;
	context?: Context;
	globalEventNamespaces?: string[];
	fieldsToAddInPublishedData?: string[];
	alertsToBeLinked?: string[];
	onIssueCreate?: null | ((data: CreatedIssueWithIssueData, alertsToBeLinked?: string[]) => void);
	onClose?: null | ((data: CreatedIssueWithIssueData[]) => void);
	/**
	 * Controls whether GIC creates an issue on submit or fires onSaveEditsWithoutIssueCreation.
	 * Defaults to 'createIssue' if not provided.
	 */
	submitBehaviour?: IssueCreateSubmitBehaviour;
	/**
	 * Called with the full form field payload when submitBehaviour is
	 * 'saveEditsWithoutIssueCreation' and the user clicks the submit button.
	 * No issue is created in this case.
	 */
	onSaveEditsWithoutIssueCreation?: (fields: FormFieldValues) => void;
	displayOnlyRequiredFields?: boolean;
	displaySitePicker?: boolean;
	disableCreationSuccessFlag?: boolean;
	displayCreateAnotherIssueToggle?: boolean;
	disableProjectDropdown?: boolean;
	disableIssueTypeDropdown?: boolean;
	triggerPointKey: TriggerPointKeyType;
	callbackPayload?: CallbackPayload;
	isModalLoadInContext?: boolean;
	disableMinimizableModal?: boolean;
	submitErrorMessage?: string;
	displayMinimizableButtons?: boolean;
	displayHeaderActionContainer?: boolean;
	onSiteSelection?: ((cloudId: string) => void) | null;
	onProjectSelection?: ((project: Project) => void) | null;
	minimizableModalView?: MinimizableModalView;
	overrideUseRequestTypeFieldsToggleState?: boolean;
	containerStyle?: ContainerStyle;
	isMiniDesignEnabled?: boolean;
	sessionId?: string;
	FieldWrapperComponent?: FieldWrapperComponentType;
	ExternalControls?: ExternalControlsType;
	jqlSwimlaneId?: string;
	prefilledCustomFields?: Field;
	ecosystemCallback?: { callbackFunction: Function };
	product?: string;
	actionSource?: string;
	triggeredViaHotKey?: boolean;
	optOutSmartFieldIds?: string[];
	addChildWorkItemTriggerSource?: string;
	returnFocusRef?: RefObject<HTMLElement>;
};

export type FieldWrapperComponentType = React.FC<
	PropsWithChildren<{
		fieldId: string;
		field: FieldConfig[string];
		setFieldValue: (name: string, value: unknown) => void;
	}>
>;
